import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Scale, Loader2, Check, X, ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function DeuxVerites({ onDiscuss, onBack }) {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);

  const play = async (surprise = false) => {
    if (loading) return;
    setLoading(true); setData(null); setPicked(null);
    try {
      const { data: res } = await api.get("/game/two-truths", {
        params: { lang: (i18n.language || "fr").slice(0, 2), topic: surprise ? "" : topic.trim() },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("games.twoTruths.error"));
    } finally { setLoading(false); }
  };

  const revealed = picked !== null;
  const pickedCorrect = revealed && data && data.statements[picked] && data.statements[picked].truth === false;

  const discuss = () => {
    if (!data) return;
    onDiscuss?.(t("games.twoTruths.discuss", { topic: data.topic || "?" }));
  };

  return (
    <div data-testid="two-truths">
      <div className="flex items-center justify-between mb-8">
        {onBack ? (
          <button onClick={onBack} data-testid="twotruths-back-btn" className="text-sm text-foreground/45 hover:text-primary transition-colors">
            {t("games.back")}
          </button>
        ) : <span />}
        <span className="text-xs uppercase tracking-[0.2em] text-primary/70">{t("games.twoTruths.name")}</span>
      </div>

      <div className="text-center mb-7">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Scale className="text-primary" size={24} strokeWidth={1.6} />
        </div>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl italic mb-2">{t("games.twoTruths.name")}</h2>
        <p className="text-foreground/55 leading-relaxed max-w-md mx-auto text-sm">{t("games.twoTruths.tagline")}</p>
      </div>

      {!data && (
        <div className="max-w-md mx-auto">
          <input
            value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder={t("games.twoTruths.topicPlaceholder")}
            data-testid="twotruths-topic-input"
            className="w-full rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-sm text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors mb-3"
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={() => play(false)} disabled={loading || !topic.trim()} data-testid="twotruths-play-topic"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {t("games.twoTruths.play")}
            </button>
            <button onClick={() => play(true)} disabled={loading} data-testid="twotruths-surprise"
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm text-foreground/70 border border-white/12 hover:text-primary hover:border-primary/30 transition-colors disabled:opacity-50">
              {t("games.twoTruths.surprise")}
            </button>
          </div>
        </div>
      )}

      {loading && !data && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-6" data-testid="twotruths-loading">
          <Loader2 size={16} className="animate-spin" /> {t("games.twoTruths.loading")}
        </p>
      )}

      {data && (
        <div className="max-w-xl mx-auto">
          {!revealed && <p className="text-center text-sm text-foreground/55 mb-4">{t("games.twoTruths.instruction")}</p>}
          <div className="space-y-3">
            {data.statements.map((s, i) => {
              const isLie = s.truth === false;
              let cls = "border-white/10 bg-white/[0.02] hover:border-primary/30";
              if (revealed) {
                if (isLie) cls = "border-red-400/50 bg-red-400/[0.06]";
                else cls = "border-emerald-400/30 bg-emerald-400/[0.04]";
              } else if (picked === i) {
                cls = "border-primary/50 bg-primary/[0.06]";
              }
              return (
                <button
                  key={i}
                  onClick={() => !revealed && setPicked(i)}
                  disabled={revealed}
                  data-testid={`twotruths-statement-${i}`}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${cls} ${revealed ? "cursor-default" : "cursor-pointer"}`}
                >
                  <div className="flex items-start gap-3">
                    {revealed && (
                      <span className={`mt-0.5 flex-shrink-0 ${isLie ? "text-red-400" : "text-emerald-400"}`}>
                        {isLie ? <X size={18} /> : <Check size={18} />}
                      </span>
                    )}
                    <div>
                      <p className="text-foreground/90 leading-relaxed text-sm sm:text-base">{s.text}</p>
                      {revealed && (
                        <p className="mt-2 text-xs leading-relaxed text-foreground/55">
                          <span className={`font-semibold uppercase tracking-wide mr-1.5 ${isLie ? "text-red-400" : "text-emerald-400"}`}>
                            {isLie ? t("games.twoTruths.lie") : t("games.twoTruths.truth")} ·
                          </span>
                          {s.note}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <AnimatePresence>
            {revealed && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                <p className={`text-center font-display text-lg mb-5 ${pickedCorrect ? "text-emerald-400" : "text-red-400"}`} data-testid="twotruths-verdict">
                  {pickedCorrect ? t("games.twoTruths.correct") : t("games.twoTruths.wrong")}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button onClick={discuss} data-testid="twotruths-discuss"
                    className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                    {t("games.twoTruths.understand")} <ArrowRight size={15} />
                  </button>
                  <button onClick={() => play(!topic.trim())} data-testid="twotruths-again"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/60 border border-white/12 hover:text-primary hover:border-primary/30 transition-colors">
                    <RotateCcw size={15} /> {t("games.twoTruths.again")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
